import React, { useState, useEffect, useMemo } from 'react';
import { Layout } from '../components/Layout';
import paymentService from '../services/paymentService';
import userService from '../services/userService';
import { DashboardPagos, User, ComisionTipo, UserRole } from '../types';
import { formatCurrency } from '../utils/format';
import { format, startOfWeek, endOfWeek } from 'date-fns';
import { es } from 'date-fns/locale';
import { Modal } from '../components/common/Modal';
import {
  BanknotesIcon,
  CalendarDaysIcon,
  FunnelIcon,
  TicketIcon,
  ScissorsIcon,
  ChevronRightIcon,
  UserCircleIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  CheckBadgeIcon
} from '@heroicons/react/24/outline';

const ComisionesPage: React.FC = () => {
  const [dashboard, setDashboard] = useState<DashboardPagos | null>(null);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPersonnelId, setSelectedPersonnelId] = useState<string | null>(null);
  const [annualStats, setAnnualStats] = useState<any>(null);
  const [loadingStats, setLoadingStats] = useState(false);

  useEffect(() => {
    if (isModalOpen && selectedPersonnelId) {
      setLoadingStats(true);
      userService.getUserStats(selectedPersonnelId)
        .then(data => {
          // Si el servicio devuelve toda la respuesta o solo los datos depende de userService.ts, pero típicamente es data.stats
          setAnnualStats((data as any).stats || data);
        })
        .catch(console.error)
        .finally(() => setLoadingStats(false));
    } else {
      setAnnualStats(null);
    }
  }, [isModalOpen, selectedPersonnelId]);

  // Filters
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const day = today.getDay();
    const monday = new Date(today);
    const diffToMonday = day === 0 ? -6 : 1 - day;
    monday.setDate(today.getDate() + diffToMonday);
    const saturday = new Date(monday);
    saturday.setDate(monday.getDate() + 5);

    const formatDate = (date: Date) => {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    };

    return { inicio: formatDate(monday), fin: formatDate(saturday) };
  });
  
  const [selectedUserFilter, setSelectedUserFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState<'ALL' | ComisionTipo>('ALL');

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [dateRange]);

  const loadInitialData = async () => {
    try {
      const users = await userService.getAllUsers();
      setAllUsers(users);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const data = await paymentService.getDashboard(undefined, undefined, dateRange.inicio, dateRange.fin);
      setDashboard(data);
    } catch (error) {
      console.error('Error al cargar dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const setQuickRange = (type: 'this-week' | 'last-week') => {
    const today = new Date();
    const day = today.getDay();
    const diffToMonday = day === 0 ? -6 : 1 - day;
    const start = new Date(today);
    start.setDate(today.getDate() + diffToMonday);
    
    if (type === 'last-week') start.setDate(start.getDate() - 7);

    const end = new Date(start);
    end.setDate(start.getDate() + 5);

    const formatDate = (date: Date) => {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    };

    setDateRange({ inicio: formatDate(start), fin: formatDate(end) });
  };

  const userSummaries = useMemo(() => {
    if (!dashboard) return [];
    const summaries: Record<string, {
      userId: string;
      nombre: string;
      rol: UserRole;
      totalComision: number;
      cantidadMovimientos: number;
    }> = {};

    dashboard.comisiones.forEach(c => {
      const uid = c.usuario_id;
      if (!summaries[uid]) {
        summaries[uid] = {
          userId: uid,
          nombre: (c.Usuario?.nombre_completo || c.Usuario?.username || 'Desconocido'),
          rol: c.Usuario?.rol_usr || UserRole.VENDEDORA,
          totalComision: 0,
          cantidadMovimientos: 0
        };
      }
      summaries[uid].totalComision += Number(c.monto);
      summaries[uid].cantidadMovimientos += 1;
    });

    return Object.values(summaries).sort((a, b) => b.totalComision - a.totalComision);
  }, [dashboard]);

  const filteredSummaries = userSummaries.filter(s => 
    s.nombre.toLowerCase().includes(selectedUserFilter.toLowerCase())
  );

  const getDetailedComisiones = () => {
    if (!dashboard) return [];
    return dashboard.comisiones.filter(c => {
      const userMatch = !selectedPersonnelId || c.usuario_id === selectedPersonnelId;
      const typeMatch = typeFilter === 'ALL' || c.tipo === typeFilter;
      return userMatch && typeMatch;
    });
  };

  const selectedUser = useMemo(() => 
    allUsers.find(u => u.id_usuario.toString() === selectedPersonnelId),
    [selectedPersonnelId, allUsers]
  );

  const modalGroupedData = useMemo(() => {
    const detailed = getDetailedComisiones();
    const groups: any = {};

    detailed.forEach(row => {
      const rDate = new Date(row.fecha_calculo);
      const monthName = format(rDate, 'MMMM yyyy', { locale: es });
      
      const weekStart = startOfWeek(rDate, { weekStartsOn: 1 });
      const weekEnd = endOfWeek(rDate, { weekStartsOn: 1 });
      const weekRange = `${format(weekStart, 'dd MMM', { locale: es })} - ${format(weekEnd, 'dd MMM', { locale: es })}`;

      if (!groups[monthName]) groups[monthName] = { name: monthName, total: 0, weeks: {} };
      if (!groups[monthName].weeks[weekRange]) groups[monthName].weeks[weekRange] = { range: weekRange, total: 0, items: [] };

      groups[monthName].total += Number(row.monto);
      groups[monthName].weeks[weekRange].total += Number(row.monto);
      groups[monthName].weeks[weekRange].items.push(row);
    });

    return groups;
  }, [getDetailedComisiones, selectedPersonnelId, dashboard, typeFilter]);

  const getRoleBadgeColor = (role: UserRole) => {
    switch (role) {
      case UserRole.ADMIN: return 'bg-purple-100 text-purple-800';
      case UserRole.VENDEDORA: return 'bg-blue-100 text-blue-800';
      case UserRole.VENDEDORA_EN_LINEA: return 'bg-cyan-100 text-cyan-800';
      case UserRole.MODISTA: return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Layout>
      <div className="-mx-4 px-4 py-6 lg:-mx-8 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <BanknotesIcon className="w-8 h-8 text-blue-600" />
              Gestión de Comisiones
            </h1>
            <p className="text-sm text-gray-500 mt-1">Reporte detallado de ingresos por vendedora y modista.</p>
          </div>
          
          <div className="flex flex-wrap items-center gap-3 bg-white p-2 rounded-xl shadow-sm border border-gray-100 w-full sm:w-auto">
             <div className="flex bg-gray-100 p-1 rounded-lg">
                <button 
                   onClick={() => setQuickRange('last-week')}
                   className="px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-white hover:shadow-sm rounded-md transition-all"
                >
                   Semana Anterior
                </button>
                <button 
                   onClick={() => setQuickRange('this-week')}
                   className="px-3 py-1.5 text-xs font-medium text-blue-600 bg-white shadow-sm rounded-md transition-all"
                >
                   Esta Semana
                </button>
             </div>
             <div className="h-6 w-px bg-gray-200" />
             <div className="flex items-center gap-2">
                <input 
                   type="date"
                   value={dateRange.inicio}
                   onChange={(e) => setDateRange({ ...dateRange, inicio: e.target.value })}
                   className="px-2 py-1 text-xs border border-gray-300 rounded-md bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 border-none font-medium"
                />
                <span className="text-gray-400 text-xs">al</span>
                <input 
                   type="date"
                   value={dateRange.fin}
                   onChange={(e) => setDateRange({ ...dateRange, fin: e.target.value })}
                   className="px-2 py-1 text-xs border border-gray-300 rounded-md bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 border-none font-medium"
                />
             </div>
          </div>
        </div>

        {/* Totales Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-500 uppercase tracking-wider">Total del Periodo</span>
              <div className="p-2 bg-blue-50 rounded-lg"><BanknotesIcon className="w-5 h-5 text-blue-600" /></div>
            </div>
            <div className="text-2xl font-bold text-gray-900">{formatCurrency(dashboard?.totales.monto_total || 0)}</div>
            <div className="mt-2 text-xs text-gray-500 flex items-center gap-1">
              <CheckBadgeIcon className="w-4 h-4 text-green-500" />
              {dashboard?.totales.comisiones || 0} movimientos validados
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-500 uppercase tracking-wider">Ventas (Vendedoras)</span>
              <div className="p-2 bg-emerald-50 rounded-lg"><TicketIcon className="w-5 h-5 text-emerald-600" /></div>
            </div>
            <div className="text-2xl font-bold text-gray-900">{formatCurrency(dashboard?.por_tipo.ventas.monto || 0)}</div>
            <div className="mt-2 text-xs text-gray-500">{dashboard?.por_tipo.ventas.cantidad || 0} folios registrados</div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-500 uppercase tracking-wider">Cortes (Modistas)</span>
              <div className="p-2 bg-amber-50 rounded-lg"><ScissorsIcon className="w-5 h-5 text-amber-600" /></div>
            </div>
            <div className="text-2xl font-bold text-gray-900">{formatCurrency(dashboard?.por_tipo.tickets.monto || 0)}</div>
            <div className="mt-2 text-xs text-gray-500">{dashboard?.por_tipo.tickets.cantidad || 0} tickets liquidados</div>
          </div>
        </div>

        {/* Search */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="relative">
            <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por nombre vendedora o modista..."
              value={selectedUserFilter}
              onChange={(e) => setSelectedUserFilter(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Grid Summary */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {loading ? (
             <div className="col-span-full py-20 text-center">
              <ArrowPathIcon className="w-10 h-10 text-blue-600 animate-spin mx-auto mb-4" />
              <p className="text-gray-500">Cargando datos del periodo...</p>
             </div>
          ) : filteredSummaries.length === 0 ? (
            <div className="col-span-full py-12 bg-white rounded-xl border border-dashed border-gray-300 text-center">
              <CalendarDaysIcon className="w-12 h-12 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500">No hay movimientos registrados para este rango.</p>
            </div>
          ) : (
            filteredSummaries.map((s) => (
              <div 
                key={s.userId}
                className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-all overflow-hidden"
              >
                <div className="p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <UserCircleIcon className="w-12 h-12 text-gray-400" />
                    <div>
                      <h3 className="font-bold text-gray-900 line-clamp-1">{s.nombre}</h3>
                      <span className={`px-2 py-0.5 text-[10px] uppercase font-bold rounded-full ${getRoleBadgeColor(s.rol)}`}>
                        {s.rol}
                      </span>
                    </div>
                  </div>
                  
                  <div className="space-y-4 pt-4 border-t border-gray-100">
                    <div>
                      <span className="text-xs text-gray-500 uppercase tracking-widest font-bold">Total a Liquidar</span>
                      <div className="text-2xl font-bold text-blue-600">{formatCurrency(s.totalComision)}</div>
                    </div>
                    
                    <button 
                      onClick={() => { setSelectedPersonnelId(s.userId); setIsModalOpen(true); }}
                      className="w-full flex items-center justify-center gap-2 py-2 bg-gray-50 text-gray-700 hover:bg-blue-50 hover:text-blue-700 rounded-lg text-sm font-semibold transition-colors border border-gray-200"
                    >
                      Ver Detalle Histórico
                      <ChevronRightIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Detail Modal */}
        <Modal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title={
            <div className="flex items-center gap-3">
               <UserCircleIcon className="w-8 h-8 text-gray-400" />
               <div className="flex flex-col">
                  <span className="text-lg font-bold text-gray-900 leading-none">{selectedUser?.nombre_completo || selectedUser?.username}</span>
                  <span className="text-xs text-gray-500 mt-1 uppercase tracking-widest font-bold">{selectedUser?.rol_usr} • {dateRange.inicio} AL {dateRange.fin}</span>
               </div>
            </div>
          }
          size="full"
        >
          <div className="flex flex-col gap-6">
            {loadingStats ? (
              <div className="flex justify-center p-4">
                <ArrowPathIcon className="w-6 h-6 animate-spin text-blue-500" />
              </div>
            ) : annualStats && (selectedUser?.rol_usr === UserRole.VENDEDORA || selectedUser?.rol_usr === UserRole.VENDEDORA_EN_LINEA) ? (
              <div className="bg-gradient-to-r from-blue-900 to-blue-800 rounded-xl p-5 text-white shadow-md">
                <div className="flex items-center gap-2 mb-4">
                  <CheckBadgeIcon className="w-5 h-5 text-blue-300" />
                  <h3 className="text-sm font-bold uppercase tracking-widest text-blue-100">Estadísticas Anuales (Últimos 12 Meses)</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="bg-white/10 rounded-lg p-3">
                    <span className="text-xs text-blue-200 uppercase block mb-1">Total Generado</span>
                    <span className="text-xl font-black">{formatCurrency(annualStats.monto_comisiones_ultimos_12_meses || 0)}</span>
                  </div>
                  <div className="bg-white/10 rounded-lg p-3">
                    <span className="text-xs text-blue-200 uppercase block mb-1">Promedio Mensual</span>
                    <span className="text-xl font-black">{formatCurrency(annualStats.promedio_mensual_comisiones || 0)}</span>
                  </div>
                  <div className="bg-white/10 rounded-lg p-3 border border-blue-400/30">
                    <span className="text-xs text-blue-100 uppercase block mb-1 font-bold">Promedio Diario *</span>
                    <span className="text-xl font-black text-blue-50">{formatCurrency(annualStats.promedio_diario_comisiones || 0)}</span>
                  </div>
                </div>
                <p className="text-[10px] text-blue-300 mt-3 text-right">* Base para cálculo de prima vacacional u otras prestaciones.</p>
              </div>
            ) : null}

            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 bg-gray-50 rounded-xl border border-gray-200">
               <div className="flex items-center gap-3">
                  <FunnelIcon className="w-5 h-5 text-gray-400" />
                  <select
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value as any)}
                    className="bg-transparent border-none focus:ring-0 text-sm font-bold text-gray-600 uppercase cursor-pointer"
                  >
                    <option value="ALL">Todos los Movimientos</option>
                    <option value="VENTA">Solo Ventas (Folio)</option>
                    <option value="TICKET">Solo Costuras (Ticket)</option>
                  </select>
               </div>
               <div className="text-right">
                  <span className="text-xs text-gray-500 uppercase font-bold block mb-1">Total Movimientos</span>
                  <span className="text-2xl font-bold text-blue-600">{formatCurrency(getDetailedComisiones().reduce((sum, c) => sum + Number(c.monto), 0))}</span>
               </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
               <table className="min-w-full">
                  <tbody className="divide-y divide-gray-100">
                    {Object.values(modalGroupedData).length === 0 ? (
                      <tr><td colSpan={5} className="p-12 text-center text-gray-400 italic">Sin registros para mostrar.</td></tr>
                    ) : (
                      Object.values(modalGroupedData).map((month: any, mIdx: number) => (
                        <React.Fragment key={mIdx}>
                           {/* MONTH HEADER */}
                           <tr className="bg-blue-900 border-b border-blue-800">
                              <td colSpan={5} className="px-6 py-2.5">
                                 <div className="flex items-center justify-between">
                                    <span className="text-sm font-black text-white uppercase tracking-tighter">{month.name}</span>
                                    <span className="text-[11px] font-bold text-blue-200">Subtotal Mes: {formatCurrency(month.total)}</span>
                                 </div>
                              </td>
                           </tr>

                           {Object.values(month.weeks).map((week: any, wIdx: number) => (
                              <React.Fragment key={`${mIdx}-${wIdx}`}>
                                 {/* WEEK HEADER */}
                                 <tr className="bg-gray-100 border-b border-gray-200">
                                    <td colSpan={5} className="px-6 py-1.5">
                                       <div className="flex items-center justify-between">
                                          <div className="flex items-center gap-2">
                                             <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                                             <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest">Semana: {week.range}</span>
                                          </div>
                                          <span className="text-[10px] font-black text-gray-900 tracking-tight">Total Semana: {formatCurrency(week.total)}</span>
                                       </div>
                                    </td>
                                 </tr>

                                 {week.items.map((c: any) => (
                                    <React.Fragment key={c.id}>
                                       {/* PAYMENT ROW */}
                                       <tr className="hover:bg-gray-50/50 transition-colors border-b border-gray-50">
                                          <td className="px-6 py-3">
                                             <div className="flex flex-col">
                                                <span className="text-[8px] text-gray-400 font-black uppercase tracking-tighter mb-0.5">Folio</span>
                                                <span className="text-xs font-bold text-blue-700 uppercase">
                                                   {c.tipo === 'VENTA' ? (c.sales?.folio || 'S/F') : `TK#${(c as any).tickets?.id || '?'}`}
                                                </span>
                                             </div>
                                          </td>
                                          <td className="px-6 py-3">
                                             <div className="flex flex-col">
                                                <span className="text-[8px] text-gray-400 font-black uppercase tracking-tighter mb-0.5">Fecha</span>
                                                <span className="text-[11px] font-bold text-gray-600">
                                                   {format(new Date(c.fecha_calculo), 'dd MMM yyyy', { locale: es })}
                                                </span>
                                             </div>
                                          </td>
                                          <td className="px-6 py-3 text-right">
                                             <div className="flex flex-col">
                                                <span className="text-[8px] text-gray-400 font-black uppercase tracking-tighter mb-0.5">Monto Pago</span>
                                                <span className="text-xs font-black text-gray-900">{formatCurrency(c.payment?.amount || 0)}</span>
                                             </div>
                                          </td>
                                          <td className="px-6 py-3 text-right">
                                             <div className="flex flex-col">
                                                <span className="text-[8px] text-gray-400 font-black uppercase tracking-tighter mb-0.5">Porcentaje</span>
                                                <span className="text-xs font-bold text-gray-500">{c.porcentaje}%</span>
                                             </div>
                                          </td>
                                          <td className="px-6 py-3 text-right">
                                             <div className="flex flex-col">
                                                <span className="text-[8px] text-red-400 font-black uppercase tracking-tighter mb-0.5 underline decoration-red-200">Comisión</span>
                                                <span className="text-sm font-black text-green-600">{formatCurrency(c.monto)}</span>
                                             </div>
                                          </td>
                                       </tr>

                                       {/* PRENDAS ROWS */}
                                       {c.prendas && c.prendas.map((p: any, pidx: number) => (
                                          <tr key={`${c.id}-${pidx}`} className="bg-gray-50/30 border-b border-gray-50/50 text-[10px] hover:bg-white transition-colors">
                                             <td className="px-6 py-1 text-center text-gray-300 font-mono w-10">↳</td>
                                             <td colSpan={2} className="px-6 py-1">
                                                <div className="flex items-center gap-3">
                                                   <span className="font-black text-gray-400 uppercase tracking-tighter">{p.marca}</span>
                                                   <span className="font-bold text-gray-700">{p.modelo}</span>
                                                </div>
                                             </td>
                                             <td className="px-6 py-1 text-center bg-white/50 border-x border-gray-50">
                                                <div className="flex items-center justify-center gap-4">
                                                   <span className="font-black text-gray-500 uppercase tracking-tighter">T: {p.talla}</span>
                                                   <span className="font-black text-gray-500 uppercase tracking-tighter">C: {p.color}</span>
                                                </div>
                                             </td>
                                             <td className="px-6 py-1 text-right font-black text-gray-600 italic">
                                                {formatCurrency(p.precio)}
                                             </td>
                                          </tr>
                                       ))}
                                    </React.Fragment>
                                 ))}
                              </React.Fragment>
                           ))}
                        </React.Fragment>
                      ))
                    )}
                  </tbody>
               </table>
            </div>

            <div className="bg-blue-950 text-white rounded-2xl p-8 shadow-2xl shadow-blue-900/20 flex flex-col sm:flex-row justify-between items-center gap-6 border border-blue-900">
               <div className="flex items-center gap-5">
                  <div className="p-4 bg-blue-600 rounded-2xl shadow-lg shadow-blue-500/20"><CalendarDaysIcon className="w-8 h-8 text-white" /></div>
                  <div>
                    <h4 className="font-black uppercase tracking-[0.2em] text-sm text-blue-400">Resumen Total de Liquidación</h4>
                    <p className="text-xs text-blue-200/60 mt-1">Suma acumulada de comisiones devengadas en el periodo.</p>
                  </div>
               </div>
               <div className="text-center sm:text-right">
                  <div className="text-5xl font-black tracking-tighter text-white drop-shadow-sm">
                    {formatCurrency(getDetailedComisiones().reduce((sum, c) => sum + Number(c.monto), 0))}
                  </div>
               </div>
            </div>
          </div>
        </Modal>
      </div>
    </Layout>
  );
};

export default ComisionesPage;